import { useCallback, useEffect, useMemo, useState } from 'react'
import { PARTIAL_NOTE, PARTIAL_TITLE, WITHHELD, WITHHELD_TITLE, partialOf } from '../api/answer'
import type { Api } from '../api'
import { ApiError, USD, LOCAL, isConverted } from '../api/types'
import type { ApiFailure, Currency, WeekDim, WeekMetric, WeekTrend } from '../api/types'
import { EmptyState, Segmented, Loading, compact, money, useSvgTextScale, currencyNote } from './ui'
import { Explain } from './DevModeContext'
import { FailureState } from './FailureState'
import { ExportButtons } from './ExportButtons'
import { SortHeader, compareBy, useGridSort, type SortState } from './gridSort'
import { chartFromSvg, type ExportDoc, type ExportRow } from '../export/model'

/**
 * TRENDS — one metric over twelve weeks.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS PAGE CAN AND CANNOT HONESTLY SAY
 *
 * Two of the four metrics are RECONSTRUCTIONS, not snapshots. Nothing stores
 * what the balance was eleven weeks ago; the procedure rebuilds it by removing
 * everything posted since. That is exact for the recent weeks anyone actually
 * looks at and drifts for a distant one, so the caption says which kind of
 * number is on the screen rather than leaving the reader to assume.
 *
 * In LOCAL currency a series can combine several functional currencies — a
 * business team spanning Saudi and Egyptian companies sums riyals and pounds.
 * The line still has a real shape, but its LEVEL is not a quantity of anything.
 * The caption says so, in those words, whenever local is selected.
 *
 * Collections has no US dollar figure in the source at all — measured: 27
 * columns, no rate, no dollar twin. Asked for USD, the procedure serves the
 * LOCAL figure, matching the report this replaces (decided 5 Aug 2026). The
 * caption below then carries the entire warning, which makes it part of the
 * number rather than a note beside it.
 */

const WEEKS = 12
const TOP_SERIES = 6 // the palette holds six; everything past that folds to "Other"

const METRICS: { value: WeekMetric; label: string }[] = [
  { value: 'collections', label: 'Collections' },
  { value: 'sales', label: 'Sales' },
  { value: 'receivables', label: 'Receivables' },
  { value: 'overdue', label: 'Overdue' },
]

type GroupBy = WeekDim | 'Total'
const GROUPS: { value: GroupBy; label: string }[] = [
  { value: 'Total', label: 'Total' },
  { value: 'BusinessUnit', label: 'Business Unit' },
  { value: 'BusinessTeam', label: 'Business Team' },
  { value: 'Company', label: 'Company' },
  { value: 'Salesperson', label: 'Salesperson' },
]

const PALETTE = [
  'var(--series-1)',
  'var(--series-2)',
  'var(--series-3)',
  'var(--series-4)',
  'var(--series-5)',
  'var(--series-6)',
]

/** A flow metric measures activity inside the week; an as-of one measures a balance at its end. */
const IS_FLOW = (m: WeekMetric) => m === 'collections' || m === 'sales'

interface Series {
  name: string
  color: string
  values: number[]
}

/**
 * One line of the summary panel: a series, its first and last week, and the
 * move between them.
 *
 * Computed once in the component rather than inside the table's own map,
 * because the extract has to ship the SAME rows in the SAME order the panel
 * renders, and two places deriving "the change" independently is how the two
 * drift apart.
 *
 * THE SERIES OBJECT RIDES ALONG, not its index. `color` lives on the series
 * itself, so reordering these rows cannot repaint a line or move a swatch away
 * from the line it names.
 */
interface SummaryRow {
  s: Series
  a: number
  b: number
  delta: number
  pct: number | null
}

/** Every heading sorts, the Series column included — it sorts by its name. */
type TrendSortKey = 'series' | 'first' | 'last' | 'change' | 'pct'

export function TrendChart({
  api,
  viewAs = null,
  // Defaults to USD with the pages (23 Sep 2026); every parent passes it.
  currency = USD,
}: {
  api: Api
  viewAs?: string | null
  currency?: Currency
}) {
  const [metric, setMetric] = useState<WeekMetric>('collections')
  const [groupBy, setGroupBy] = useState<GroupBy>('Total')
  const [data, setData] = useState<WeekTrend | null>(null)
  /** The new permission model answered (M8). */
  const [v2, setV2] = useState(false)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [loading, setLoading] = useState(true)
  const [hover, setHover] = useState<number | null>(null)
  /* No initial key: the panel OPENS mirroring the plot — the series in the
     order they were ranked and coloured — which is not any one column's order.
     So "no column sorted" is the natural state and the third click restores it. */
  const { sort, toggle, ariaSort } = useGridSort<TrendSortKey>()

  // "Total" is not a dimension the database has; it is Company, summed. Company
  // is the safest carrier because every fact table has one and it needs no
  // column permission.
  const dim: WeekDim = groupBy === 'Total' ? 'Company' : groupBy
  const available = !(metric === 'sales' && dim === 'BusinessUnit')

  const load = useCallback(async () => {
    setLoading(true)
    setFailure(null)
    try {
      const res = await api.receivablesTrend({ metric, dim, weeks: WEEKS, currency })
      setData(res.data)
      setV2(res.vis !== null)
    } catch (e: unknown) {
      setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Unexpected error.' })
    } finally {
      setLoading(false)
    }
  }, [api, metric, dim, currency, viewAs])

  useEffect(() => {
    if (available) void load()
    else setLoading(false)
  }, [load, available])

  const weeks = data?.weeks ?? []


  const series = useMemo<Series[]>(() => {
    if (weeks.length === 0) return []

    if (groupBy === 'Total') {
      return [
        {
          name: METRICS.find((m) => m.value === metric)!.label,
          color: PALETTE[0],
          values: weeks.map((w) => w.members.reduce((s, m) => s + (m.amount ?? 0), 0)),
        },
      ]
    }

    // Union of every member seen in any week, ranked by its twelve-week total.
    // Union rather than "members of the latest week": a team that collected
    // heavily for ten weeks and then stopped is precisely what a trend is for,
    // and ranking on the last week alone would drop it.
    const totals = new Map<string, number>()
    for (const w of weeks)
      for (const m of w.members) {
        const k = m.member ?? '(blank)'
        totals.set(k, (totals.get(k) ?? 0) + (m.amount ?? 0))
      }
    const ranked = [...totals.entries()].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])).map(([n]) => n)
    const top = ranked.slice(0, TOP_SERIES)
    const rest = new Set(ranked.slice(TOP_SERIES))

    const byWeek = weeks.map((w) => {
      const m = new Map<string, number>()
      for (const x of w.members) m.set(x.member ?? '(blank)', x.amount ?? 0)
      return m
    })

    const out: Series[] = top.map((name, i) => ({
      name,
      color: PALETTE[i % PALETTE.length],
      // Absent from a week is genuinely zero for both kinds of metric: no
      // activity for a flow, no balance for an as-of.
      values: byWeek.map((m) => m.get(name) ?? 0),
    }))

    if (rest.size > 0) {
      out.push({
        name: `Other (${rest.size})`,
        color: 'var(--series-other)',
        values: byWeek.map((m) => {
          let s = 0
          for (const [n, v] of m) if (rest.has(n)) s += v
          return s
        }),
      })
    }
    return out
  }, [weeks, groupBy, metric])

  const labels = weeks.map((w) => shortLabel(w.start))

  /**
   * THE SUMMARY ROWS, in the order the panel shows them.
   *
   * `series` is the NATURAL order: the six largest by twelve-week total, with
   * the folded "Other" last, and that is the order the legend and the plot
   * read in. The reader's sort is applied to a COPY of that list — the array
   * the chart plots is never touched, so a sort can reorder the table and
   * nothing else. A third click drops `sort.key` and hands the ranking back.
   *
   * The tie-break is the series name, ascending in both directions, so two
   * series that moved by the same amount do not swap places between renders.
   */
  const summaryRows: SummaryRow[] = series.map((s) => {
    const a = s.values[0]
    const b = s.values[s.values.length - 1]
    const delta = b - a
    const pct = a !== 0 && Number.isFinite(delta / Math.abs(a)) ? delta / Math.abs(a) : null
    return { s, a, b, delta, pct }
  })
  const orderedRows: SummaryRow[] = sort.key
    ? [...summaryRows].sort((x, y) => {
        const k = sort.key as TrendSortKey
        const v = (r: SummaryRow) =>
          k === 'series' ? r.s.name : k === 'first' ? r.a : k === 'last' ? r.b : k === 'change' ? r.delta : r.pct
        return compareBy(v(x), v(y), sort.desc, () => x.s.name.localeCompare(y.s.name))
      })
    : summaryRows

  /* NO EARLY RETURN ON FAILURE ANY MORE — the failure renders in the chart's
     place below, and the controls stay. See the render chain. */

  /* THE EXTRACT — the chart rasterized from the very SVG on screen, then the
     same series summary table (six largest + the folded rest) with the same
     change column. Async because the rasterizing is; a failed rasterize ships
     the table alone. Collections is stamped LOCAL whatever the toggle says —
     the source has no dollar figure — and the caption travels as context. */
  const buildDoc = async (): Promise<ExportDoc | null> => {
    // `failure` too: after a refused regrouping, `data` can still hold the
    // PREVIOUS grouping's series, and the extract must be the screen — which
    // is showing a refusal, not a chart.
    if (loading || failure || !available || !data) return null
    if (series.length === 0 || series.every((s) => s.values.every((v) => v === 0))) return null
    const metricLabel = METRICS.find((m) => m.value === metric)?.label ?? metric
    const groupLabel = GROUPS.find((g) => g.value === groupBy)?.label ?? groupBy
    const localOnly = metric === 'collections' && isConverted(currency)
    const chart = await chartFromSvg(
      document.getElementById('recv-trend-svg') as SVGSVGElement | null,
      `${metricLabel} by week, ${labels[0]} – ${labels[labels.length - 1]}${
        groupBy === 'Total' ? '' : `, by ${groupLabel.toLowerCase()}`
      }.`,
    )
    return {
      report: 'Receivables',
      page: 'Trends',
      title: `${metricLabel}, twelve weeks`,
      subtitle:
        groupBy === 'Total'
          ? 'The whole book, plotted week by week.'
          : `Split by ${groupLabel.toLowerCase()} — the six largest series drawn, the rest folded into one.`,
      context: [
        `Measure: ${metricLabel} · grouped by ${groupLabel}`,
        `Weeks: ${labels[0]} – ${labels[labels.length - 1]}, Sunday to Saturday`,
        IS_FLOW(metric)
          ? 'Activity inside each week.'
          : 'Balance as it stood at each week end, rebuilt from the current ledger — exact for recent weeks, approximate for distant ones.',
        localOnly
          ? 'Shown in local currency: collections has no US dollar figure in the source.'
          : `Currency: ${currencyNote(currency)}`,
        ...(!isConverted(currency) && groupBy !== 'Company'
          ? ['Local currency: a series may combine several currencies, so read the shape rather than the level.']
          : []),
        /* Said outright, because the sheet is read away from the screen that
           ordered it: without this line a table no longer in series rank looks
           like a table whose ranking is wrong. */
        ...(sort.key
          ? [
              `Table sorted by ${
                sort.key === 'series'
                  ? 'Series'
                  : sort.key === 'first'
                    ? labels[0]
                    : sort.key === 'last'
                      ? labels[labels.length - 1]
                      : sort.key === 'change'
                        ? 'Change'
                        : '%'
              }, ${sort.desc ? 'largest first' : 'smallest first'} — the chart keeps its own order.`,
            ]
          : []),
      ],
      privileged: viewAs,
      currency: localOnly ? LOCAL : currency,
      chart,
      tables: [
        {
          columns: [
            { header: 'Series' },
            { header: labels[0], kind: 'money' },
            { header: labels[labels.length - 1], kind: 'money' },
            { header: 'Change', kind: 'money' },
            { header: '%', kind: 'pct' },
          ],
          /* THE EXTRACT IS THE SCREEN: the summary rows in the order the panel
             renders them, sorted column and all. */
          rows: orderedRows.map<ExportRow>((r) => ({ cells: [r.s.name, r.a, r.b, r.delta, r.pct] })),
          note: 'First week against last, per series — the shape between them is the chart’s.'
            + (anyPartial ? ` ${PARTIAL_NOTE}` : ''),
        },
      ],
    }
  }

  // The shape, summed — the whole visible book at the first week and the last, so
  // the reader is handed the total rather than left to eyeball it off the lines.
  const metricLabel = METRICS.find((mm) => mm.value === metric)?.label ?? metric
  const firstTotal = series.reduce((s, sr) => s + (sr.values[0] ?? 0), 0)
  const lastTotal = series.reduce((s, sr) => s + (sr.values[sr.values.length - 1] ?? 0), 0)
  const change = lastTotal - firstTotal
  const changePct = firstTotal !== 0 ? change / Math.abs(firstTotal) : null
  /* UNDER THE NEW MODEL a member's amount can be withheld in some weeks, one
     piece of access at a time, and the lines can only draw what shows (M8). So
     the week a figure leaves anything out is marked, and a chart of nothing
     but withheld amounts says so rather than drawing a line at zero. */
  const weekPartial = weeks.map((w) => v2 && w.members.some((m) => m.amount === null || partialOf(m).size > 0))
  const anyPartial = weekPartial.some(Boolean)
  const allWithheld = v2 && weeks.length > 0 && weeks.every((w) => w.members.every((m) => m.amount === null))
  const firstPartial = weekPartial[0] ?? false
  const lastPartial = weekPartial[weekPartial.length - 1] ?? false
  const tile = (v: number, partial: boolean) => (allWithheld ? WITHHELD : `${compact(v)}${partial ? '*' : ''}`)
  const tileFull = (v: number, partial: boolean) =>
    allWithheld ? WITHHELD_TITLE : partial ? `${money(v)}. ${PARTIAL_TITLE}.` : money(v)
  // More collections/sales is good; more receivables/overdue is not — same
  // judgement the summary table makes, so the change tile is not mis-coloured.
  const goodWhenUp = metric === 'collections' || metric === 'sales'
  const changeTone: 'none' | 'good' | 'bad' = change === 0 ? 'none' : (change > 0) === goodWhenUp ? 'good' : 'bad'

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* ---------------- Controls ---------------- */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-(--color-line) px-5 py-4">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div>
            <Explain surface="filters" measureKey="metric" className="eyebrow mb-1.5 block">
              Measure
            </Explain>
            <Segmented label="Metric" value={metric} onChange={setMetric} options={METRICS} />
          </div>
          <div>
            <Explain surface="filters" measureKey="grouping" className="eyebrow mb-1.5 block">
              Group by
            </Explain>
            <Segmented label="Grouping" value={groupBy} onChange={setGroupBy} options={GROUPS} />
          </div>
        </div>
        <div className="pb-0.5">
          <ExportButtons build={buildDoc} />
        </div>
      </div>

      {/* The caption is part of the number, not decoration around it. */}
      <p className="border-b border-(--color-line) bg-(--color-surface-2) px-5 py-2.5 text-(length:--text-small) leading-relaxed text-(--color-ink-3)">
        {IS_FLOW(metric)
          ? 'Activity inside each week, Sunday to Saturday.'
          : 'Balance as it stood at each week end, rebuilt from the current ledger — exact for recent weeks, approximate for distant ones.'}
        {/* COLLECTIONS IS ALWAYS LOCAL — the source has no rate and no dollar
            column, so a USD request is served as local figures. This sentence
            is the only thing distinguishing them from converted ones, which
            makes it part of the number rather than a note about it. */}
        {metric === 'collections' && isConverted(currency) && (
          <>
            {' '}
            <span className="font-semibold text-(--color-warn)">
              Shown in local currency: collections has no US dollar figure in the source.
            </span>
          </>
        )}
        {!isConverted(currency) && groupBy !== 'Company' && (
          <>
            {' '}
            <span className="font-medium text-(--color-warn)">
              Local currency: a series may combine several currencies, so read the shape rather
              than the level.
            </span>
          </>
        )}
      </p>

      {/* THE LOADER SHOWS ON EVERY FETCH, NOT JUST THE FIRST.
          --------------------------------------------------------------------
          The condition here was `loading && !data`, so changing Measure or
          Group By — which refetches twelve weeks of a different series — showed
          nothing at all. The old chart stayed on screen at 60% opacity and the
          new one appeared some seconds later. A reader who did not notice the
          dimming was looking at the PREVIOUS measure's figures believing they
          were the one they had just chosen.

          A fade is too quiet a signal for that. It rendered a chart of
          Collections while the control said Receivables.

          It sits ALONGSIDE the chart rather than replacing it, so nothing
          reflows: BrandLoader is fixed to the centre of the screen and takes no
          space in this column. The dimming is gone because the loader now veils
          the page itself — two dimmings stacked just made the figures muddy. */}
      {loading && <Loading label={`Loading ${WEEKS} weeks…`} />}

      {/* A FAILURE TAKES THE CHART'S PLACE, NOT THE WHOLE CARD — the rule
          WeekComparison follows. A refusal here is usually caused by the Group
          by choice (Salesperson, for a reader who may not see that column), and
          the refusal's own sentence says another grouping will work. It used to
          replace the entire component, picker included, so the reader was told
          to choose another grouping and given no way to — the only way out was
          another tab. The unavailable combination is checked first because it
          sends no request, so a failure held from the previous choice is not
          the answer to this one. */}
      {!available ? (
        <EmptyState
          title="Sales cannot be grouped by business unit"
          detail="The sales table records a company and a team but no business unit, so there is nothing to group on. The other three measures support all four groupings."
        />
      ) : failure ? (
        <FailureState failure={failure} onRetry={() => void load()} />
      ) : !data ? null : allWithheld ? (
        <EmptyState
          title="Withheld"
          detail="Your access does not show you these amounts in any of the twelve weeks."
        />
      ) : series.length === 0 || series.every((s) => s.values.every((v) => v === 0)) ? (
        <EmptyState
          title="No activity in the last twelve weeks"
          detail="An empty result for the data you are permitted to see — not a refusal."
        />
      ) : (
        /* TWO PANELS, NOT A STACK — the sales Monthly Trend idiom. The chart and
           the figures sit side by side so the shape and the numbers answer each
           other, with the summed strip above the plot. */
        <div className="scroll-slim min-h-0 flex-1 overflow-y-auto px-5 py-3">
          <div className="grid min-h-0 items-start gap-4 @5xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col gap-4">
              <div className="grid grid-cols-1 gap-3 @md:grid-cols-3">
                <TrendStat label={labels[0]} value={tile(firstTotal, firstPartial)} full={tileFull(firstTotal, firstPartial)} tone="accent" />
                <TrendStat label={labels[labels.length - 1]} value={tile(lastTotal, lastPartial)} full={tileFull(lastTotal, lastPartial)} />
                <TrendStat
                  label="Change"
                  value={allWithheld ? WITHHELD : changePct === null ? '—'
                    : `${change >= 0 ? '+' : '−'}${(Math.abs(changePct) * 100).toFixed(1)}%${firstPartial || lastPartial ? '*' : ''}`}
                  full={`${change >= 0 ? '+' : '−'}${money(Math.abs(change))} from ${labels[0]} to ${labels[labels.length - 1]}`}
                  tone={changeTone}
                />
              </div>
              {anyPartial && !allWithheld ? (
                <p className="text-(length:--text-small) text-(--color-ink-3)">
                  Some weeks leave out amounts your access does not show you; the lines draw what you may see.
                </p>
              ) : null}

              <section className="rounded-md border border-(--color-line) bg-(--color-surface) p-4 shadow-(--shadow-card)">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                  <h3 className="text-(length:--text-base) font-semibold text-(--color-ink)">{metricLabel} by week</h3>
                  {/* Legend beside the title, not stranded under the plot. */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    {series.map((s) => (
                      <span
                        key={s.name}
                        className="inline-flex items-center gap-1.5 text-(length:--text-small) text-(--color-ink-2)"
                      >
                        <span className="h-[3px] w-4 rounded-full" style={{ background: s.color }} />
                        {s.name}
                      </span>
                    ))}
                  </div>
                </div>
                <Plot labels={labels} series={series} hover={hover} setHover={setHover} />
              </section>
            </div>

            <Summary
              rows={orderedRows}
              first={labels[0]}
              last={labels[labels.length - 1]}
              metric={metric}
              sort={sort}
              toggle={toggle}
              ariaSort={ariaSort}
            />
          </div>
        </div>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------------------
   The chart itself. Plain SVG on a fixed viewBox, scaled by CSS — no charting
   library. A dependency here would be 40-odd kilobytes and a supply-chain
   surface for two hundred lines of arithmetic, on a platform whose whole
   argument is that its behaviour is inspectable.
   --------------------------------------------------------------------------- */
// Sized to render near 1:1 in the left column of the two-panel layout, the same
// reasoning the sales Monthly Trend uses: at 960 wide the viewBox scaled to about
// three quarters in a column and every axis label shrank with it.
const W = 760
const H = 320
const ML = 70
const MR = 16
const MT = 12
const MB = 38

function Plot({
  labels,
  series,
  hover,
  setHover,
}: {
  labels: string[]
  series: Series[]
  hover: number | null
  setHover: (i: number | null) => void
}) {
  const plotW = W - ML - MR
  const plotH = H - MT - MB
  const n = labels.length

  const all = series.flatMap((s) => s.values)
  // Zero is always on the axis for money: a chart that starts at 4.1M turns a
  // 2% move into a cliff. The 8% headroom keeps the top line off the frame.
  const yMax = Math.max(1, ...all) * 1.08
  const yMin = Math.min(0, ...all) * 1.08

  const x = (i: number) => ML + (n <= 1 ? 0 : (i / (n - 1)) * plotW)
  const y = (v: number) => MT + plotH - ((v - yMin) / (yMax - yMin || 1)) * plotH

  const ticks = 4
  const grid = Array.from({ length: ticks + 1 }, (_, i) => yMin + ((yMax - yMin) * i) / ticks)

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * W
    const i = Math.round(((px - ML) / plotW) * (n - 1))
    setHover(Math.max(0, Math.min(n - 1, i)))
  }

  const every = n > 8 ? 2 : 1
  // The axis type keeps its stated size at every width.
  const { svgRef, k } = useSvgTextScale(W)

  return (
    <div className="relative w-full">
      <svg
        ref={svgRef}
        id="recv-trend-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`${series.length} series over ${n} weeks`}
        className="block h-auto w-full cursor-crosshair"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {grid.map((v, i) => (
          <g key={i}>
            <line x1={ML} x2={W - MR} y1={y(v)} y2={y(v)} stroke="var(--color-line)" strokeWidth={1} />
            <text
              x={ML - 10}
              y={y(v) + 3.5}
              textAnchor="end"
              fontSize={11 * k}
              fill="var(--color-ink-3)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {compact(v)}
            </text>
          </g>
        ))}

        <line x1={ML} x2={W - MR} y1={y(0)} y2={y(0)} stroke="var(--color-line-strong)" strokeWidth={1} />

        {labels.map((l, i) =>
          i % every === 0 ? (
            <text key={i} x={x(i)} y={H - MB + 20} textAnchor="middle" fontSize={10.5 * k} fill="var(--color-ink-3)">
              {l}
            </text>
          ) : null,
        )}

        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={MT}
            y2={MT + plotH}
            stroke="var(--color-navy-400)"
            strokeWidth={1}
          />
        )}

        {series.map((s) => (
          <g key={s.name}>
            <polyline
              points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {/* The most recent point is the one people look for. */}
            <circle cx={x(n - 1)} cy={y(s.values[n - 1])} r={4} fill={s.color} />
            {hover !== null && (
              <circle
                cx={x(hover)}
                cy={y(s.values[hover])}
                r={4.5}
                fill={s.color}
                stroke="var(--color-surface)"
                strokeWidth={1.5}
              />
            )}
          </g>
        ))}
      </svg>

      {hover !== null && (
        <div
          className="pointer-events-none absolute top-0 min-w-[190px] rounded-md border border-(--color-line-strong) bg-(--color-surface) px-3 py-2 text-(length:--text-small) shadow-lg"
          style={{
            left: `${(x(hover) / W) * 100}%`,
            transform: x(hover) > W / 2 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
          }}
        >
          <div className="mb-1.5 font-semibold text-(--color-ink)">{labels[hover]}</div>
          {series.map((s) => (
            <div key={s.name} className="flex items-center gap-2 py-px">
              <span className="h-2.5 w-2.5 shrink-0 rounded-[2px]" style={{ background: s.color }} />
              <span className="flex-1 truncate text-(--color-ink-2)">{s.name}</span>
              <span className="num font-semibold text-(--color-ink)">{money(s.values[hover])}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/** A compact figure for the trend's summary strip — the sales Monthly Trend's
 *  TrendStat, cloned so the two trend pages read as one design. */
function TrendStat({
  label,
  value,
  full,
  tone = 'none',
}: {
  label: string
  value: string
  full?: string
  tone?: 'none' | 'accent' | 'good' | 'bad'
}) {
  const colour = {
    none: 'text-(--color-ink)',
    accent: 'text-(--color-ink)',
    good: 'text-(--color-good)',
    bad: 'text-(--color-overdue)',
  }[tone]
  return (
    <div className="relative overflow-hidden rounded-md border border-(--color-line) bg-(--color-surface) px-3 py-2.5 shadow-(--shadow-card)">
      {tone === 'accent' && <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-(--color-navy-700)" />}
      <div className="truncate text-(length:--text-small) text-(--color-ink-3)" title={label}>
        {label}
      </div>
      <div className={`num mt-0.5 truncate text-[19px] font-semibold leading-tight ${colour}`} title={full}>
        {value}
      </div>
    </div>
  )
}

/**
 * The five headings, in order.
 *
 * `key` is the SORT key and `measureKey` the documented one; they differ for
 * the percentage column, whose documentation has always been filed under
 * `changePct`. The two week columns take their labels from the data, because
 * those headings are dates that move every time the report is opened.
 */
const SUMMARY_COLUMNS: { key: TrendSortKey; label: string; measureKey: string }[] = [
  { key: 'series', label: 'Series', measureKey: 'series' },
  { key: 'first', label: '', measureKey: 'first' },
  { key: 'last', label: '', measureKey: 'last' },
  { key: 'change', label: 'Change', measureKey: 'change' },
  { key: 'pct', label: '%', measureKey: 'changePct' },
]

/**
 * The series, as a panel beside the plot rather than a slab beneath it — the
 * sales Monthly Trend's TrendTable idiom, carrying the receivables page's own
 * content (first week against last, per series, with the change and its
 * percentage). The colour legend lives in the chart card header above.
 */
function Summary({
  rows,
  first,
  last,
  metric,
  sort,
  toggle,
  ariaSort,
}: {
  /* Already ordered by the caller. The sort lives up there because the EXTRACT
     has to ship this same order, and only the caller builds the extract. */
  rows: SummaryRow[]
  first: string
  last: string
  metric: WeekMetric
  sort: SortState<TrendSortKey>
  toggle: (key: TrendSortKey) => void
  ariaSort: (key: TrendSortKey) => 'ascending' | 'descending' | 'none'
}) {
  // Same judgement as Week Comparison: more collections and more sales is good,
  // more receivables and more overdue is not. Colouring every rise green would
  // congratulate a team whose overdue book had grown.
  const goodWhenUp = metric === 'collections' || metric === 'sales'
  const tone = (d: number) =>
    d === 0 ? 'text-(--color-ink-3)' : (d > 0) === goodWhenUp ? 'text-(--color-good)' : 'text-(--color-overdue)'

  return (
    <section className="flex min-h-0 flex-col rounded-md border border-(--color-line) bg-(--color-surface) shadow-(--shadow-card)">
      <h3 className="shrink-0 border-b border-(--color-line) px-4 py-2 text-(length:--text-base) font-semibold text-(--color-ink)">
        {first} → {last}, by series
      </h3>
      <div className="scroll-slim min-h-0 flex-1 overflow-auto">
        <table className="report-table w-full border-collapse text-(length:--text-body)">
          <thead className="sticky top-0 z-10">
            {/* `eyebrow` moved from the label to the CELL. It only ever carried
                typography — size, weight, tracking, caps, colour, all of them
                inherited — so the heading sets identically while the label
                inside it becomes SortHeader's button. */}
            <tr>
              {SUMMARY_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={ariaSort(col.key)}
                  className="eyebrow border-b border-(--color-line-strong) px-3 py-1.5 text-center"
                >
                  <SortHeader
                    label={col.key === 'first' ? first : col.key === 'last' ? last : col.label}
                    active={sort.key === col.key}
                    desc={sort.desc}
                    onToggle={() => toggle(col.key)}
                    surface="trends"
                    measureKey={col.measureKey}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ s, a, b, delta, pct }) => (
              <tr key={s.name} className="border-b border-(--color-line)">
                <td className="px-3 py-1.5">
                  {/* The swatch is read off the SERIES, never off this row's
                      position, so a sorted table still names its own line. */}
                  <span className="inline-flex items-center gap-2 text-(--color-ink)">
                    <span className="h-[3px] w-3.5 shrink-0 rounded-full" style={{ background: s.color }} />
                    <span className="truncate">{s.name}</span>
                  </span>
                </td>
                <td className="num whitespace-nowrap px-3 py-1.5 text-right text-(--color-ink-2)">{money(a)}</td>
                <td className="num whitespace-nowrap px-3 py-1.5 text-right text-(--color-ink)">{money(b)}</td>
                <td className={`num whitespace-nowrap px-3 py-1.5 text-right font-semibold ${tone(delta)}`}>
                  {delta < 0 ? `−${money(Math.abs(delta))}` : `+${money(delta)}`}
                </td>
                <td className={`num whitespace-nowrap px-3 py-1.5 text-right ${tone(delta)}`}>
                  {pct === null || !Number.isFinite(pct) ? '—' : `${pct > 0 ? '+' : ''}${(pct * 100).toFixed(1)}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

/** "06 Jul" — short enough for twelve x-axis labels on one line. */
function shortLabel(iso: string): string {
  return new Date(iso.slice(0, 10) + 'T00:00:00Z').toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  })
}
